// 1. External library imports
import { Stack, Typography, Button, Box } from '@mui/material'
import { Refresh } from '@mui/icons-material'
import type { FallbackProps } from 'react-error-boundary'

// 2. Internal imports
import { displayMessage } from '../../constants/strings'

// ErrorDisplay component - used for displaying error messages with customizable content
interface ErrorDisplayProps {
  primaryMessage: string
  secondaryMessage?: string
  showReloadOption?: boolean
  error?: Error
}

export const ErrorDisplay = ({
  primaryMessage,
  secondaryMessage,
  showReloadOption = false,
  error,
}: ErrorDisplayProps) => {
  const handleReload = () => {
    window.location.reload()
  }

  return (
    <Stack justifyContent="center" alignItems="center" sx={{ width: '100%', minHeight: '400px', py: 4 }} gap={2}>
      <Typography
        variant="h5"
        component="h1"
        sx={{
          fontWeight: 600,
          color: '#333',
          textAlign: 'center',
        }}
      >
        {primaryMessage}
      </Typography>
      {secondaryMessage && (
        <Typography
          variant="body1"
          component="p"
          sx={{
            color: '#666',
            textAlign: 'center',
            maxWidth: '500px',
          }}
        >
          {secondaryMessage}
        </Typography>
      )}
      {error && (
        <Typography
          variant="caption"
          component="pre"
          sx={{
            color: '#999',
            textAlign: 'left',
            maxWidth: '600px',
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '11px',
          }}
        >
          {error.message}
        </Typography>
      )}
      {showReloadOption && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleReload}
            sx={{
              textTransform: 'none',
              borderRadius: 1,
            }}
          >
            Reload Page
          </Button>
        </Box>
      )}
    </Stack>
  )
}

// ErrorBoundary Fallback Component
// Used with react-error-boundary library
export function ErrorBoundary({ error }: FallbackProps) {
  // Convert unknown error to Error for display
  const errorObj = error instanceof Error ? error : new Error(String(error))

  return (
    <ErrorDisplay
      primaryMessage={displayMessage.default.internalError}
      secondaryMessage="An unexpected error occurred. Please try reloading the page."
      error={errorObj}
      showReloadOption
    />
  )
}
